import React, { useState, useEffect } from 'react';
import { ideaApi } from '../services/api';
import { offlineService } from '../services/offlineService';
import toast from 'react-hot-toast';

interface QuickCaptureProps {
  onIdeaCreated: () => void;
}

export const QuickCapture: React.FC<QuickCaptureProps> = ({ onIdeaCreated }) => {
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineCount, setOfflineCount] = useState(0);

  useEffect(() => {
    const updateOfflineCount = async () => {
      const count = await offlineService.getOfflineCount();
      setOfflineCount(count);
    };

    updateOfflineCount();

    const cleanup = offlineService.onOnlineStatusChange((online) => {
      setIsOnline(online);
      if (online) {
        updateOfflineCount();
        // Ideas will sync automatically via service worker
        setTimeout(() => {
          updateOfflineCount();
          onIdeaCreated(); // Refresh the main list
        }, 2000);
      }
    });

    return cleanup;
  }, [onIdeaCreated]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;

    setIsSubmitting(true);
    try {
      if (isOnline) {
        await ideaApi.create(content);
        toast.success('Idea captured!');
        onIdeaCreated();
      } else {
        await offlineService.saveOfflineIdea(content);
        setOfflineCount(await offlineService.getOfflineCount());
        toast.success('Idea saved offline! Will sync when connected.');
      }
      setContent('');
    } catch (error) {
      toast.error('Failed to save idea');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSubmit(e);
    }
  };

  return (
    <div className="quick-capture-container">
      {!isOnline && (
        <div className="offline-indicator">
          📱 Offline mode - ideas will sync when connected
          {offlineCount > 0 && ` (${offlineCount} pending)`}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="quick-capture">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="What's on your mind? (Ctrl+Enter to save)"
          className="capture-input"
          rows={3}
          disabled={isSubmitting}
          autoFocus
        />
        <button 
          type="submit" 
          disabled={!content.trim() || isSubmitting}
          className="capture-button"
        >
          {isSubmitting ? 'Saving...' : (isOnline ? 'Capture Idea' : 'Save Offline')}
        </button>
      </form>
    </div>
  );
};